import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, ChevronDown, ChevronUp, Edit3, Plus, RotateCcw, Save, Sparkles, Trash2, X } from 'lucide-react';
import {
  archiveLearningArchiveAPI,
  deleteLearningArchiveAPI,
  draftLearningArchiveAPI,
  listLearningArchiveAPI,
  saveLearningArchiveAPI,
} from '../utils/api';

const TYPES = [
  ['feedback_lesson', 'Feedback lesson'],
  ['incident', 'Incident'],
  ['product_correction', 'Product correction'],
  ['deployment', 'Deployment'],
  ['privacy', 'Privacy'],
  ['cost', 'Cost'],
  ['admin_review', 'Admin review'],
];
const STATUSES = ['open', 'reviewing', 'resolved', 'launched', 'monitoring'];
const ENVIRONMENTS = [
  ['staging', 'Staging'],
  ['production', 'Production'],
  ['both', 'Both'],
  ['not_applicable', 'Not applicable'],
];
const blankEntry = {
  eventDate: new Date().toISOString().slice(0, 10),
  title: '',
  feature: '',
  summary: '',
  outcome: '',
  type: 'incident',
  status: 'resolved',
  environment: 'production',
  tags: [],
  relatedLink: '',
  details: '',
  lesson: '',
  nextStep: '',
  source: 'manual',
};

const formControlClass =
  'mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-500 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-500/15';

function displayLabel(value) {
  return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatEventDate(value) {
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(date);
}

function FieldBadge({ label, value, tone = 'slate' }) {
  const tones = {
    blue: 'border-sky-300 bg-sky-50 text-sky-900',
    amber: 'border-amber-300 bg-amber-50 text-amber-950',
    green: 'border-teal-300 bg-teal-50 text-teal-900',
    purple: 'border-violet-300 bg-violet-50 text-violet-900',
    slate: 'border-slate-300 bg-slate-50 text-slate-800',
  };
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <span className={`mt-1 inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${tones[tone] || tones.blue}`}>
        {displayLabel(value)}
      </span>
    </div>
  );
}

export function LearningArchivePanel({ secret, search = '' }) {
  const editorRef = useRef(null);
  const [items, setItems] = useState([]);
  const [archived, setArchived] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState(blankEntry);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [draftNotes, setDraftNotes] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [editorError, setEditorError] = useState('');
  const [tagDraft, setTagDraft] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);

  async function load(nextArchived = archived) {
    setLoading(true);
    setError('');
    try {
      setItems(await listLearningArchiveAPI({ secret, archived: nextArchived }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(false);
    // The admin secret is stable for the lifetime of this mounted panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secret]);

  useEffect(() => {
    if (!editing) return;
    window.requestAnimationFrame(() => {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      editorRef.current?.querySelector('input, select, textarea, button')?.focus({ preventScroll: true });
    });
  }, [editing]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (environmentFilter !== 'all' && item.environment !== environmentFilter) return false;
      if (!query) return true;
      return [
        item.title, item.feature, item.summary, item.outcome, item.lesson,
        item.details, item.nextStep, item.relatedLink, ...(item.tags || []),
      ].join(' ').toLowerCase().includes(query);
    });
  }, [items, search, typeFilter, statusFilter, environmentFilter]);

  function startNew() {
    setEditing('new');
    setForm({ ...blankEntry, eventDate: new Date().toISOString().slice(0, 10) });
    setError('');
    setMessage('');
    setEditorError('');
    setTagDraft('');
  }

  function startEdit(item) {
    setEditing(item.id);
    setForm({ ...blankEntry, ...item });
    setError('');
    setMessage('');
    setEditorError('');
    setTagDraft('');
  }

  async function createDraft() {
    setDrafting(true);
    setError('');
    setMessage('');
    try {
      const draft = await draftLearningArchiveAPI({ secret, notes: draftNotes });
      setForm({ ...blankEntry, ...draft, source: 'ai_draft' });
      setEditing('new');
      setDraftOpen(false);
      setDraftNotes('');
      setMessage('AI created a draft. Review every field, then save or close it.');
      setEditorError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setDrafting(false);
    }
  }

  async function save(event) {
    event.preventDefault();
    setLoading(true);
    setEditorError('');
    try {
      await saveLearningArchiveAPI({ secret, entry: { ...form, id: editing === 'new' ? undefined : editing } });
      setEditing(null);
      setMessage('Learning Archive entry saved.');
      await load();
    } catch (err) {
      setEditorError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function addTag() {
    const tag = tagDraft.trim().toLowerCase().replace(/^#/, '');
    if (!tag) return;
    if ((form.tags || []).includes(tag)) {
      setTagDraft('');
      return;
    }
    if ((form.tags || []).length >= 12) {
      setEditorError('You can add up to 12 tags.');
      return;
    }
    setForm({ ...form, tags: [...(form.tags || []), tag] });
    setTagDraft('');
    setEditorError('');
  }

  function removeTag(tag) {
    setForm({ ...form, tags: (form.tags || []).filter((item) => item !== tag) });
  }

  async function changeArchived(item, nextArchived) {
    setError('');
    try {
      await archiveLearningArchiveAPI({ secret, id: item.id, archived: nextArchived });
      setMessage(nextArchived ? 'Entry archived.' : 'Entry restored.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function permanentlyDelete(item) {
    setError('');
    try {
      await deleteLearningArchiveAPI({ secret, id: item.id });
      setConfirmingDeleteId(null);
      setMessage('Entry permanently deleted.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl shadow-black/10">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-teal-700">Company memory</p>
            <h2 className="mt-2 text-2xl font-bold text-slate-900">Learning Archive</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Keep short, reusable lessons from important fixes, decisions, corrections, and launches. Do not store emails, secrets, raw user messages, or long logs.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={() => setDraftOpen(!draftOpen)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-violet-300 bg-violet-50 px-4 py-2.5 font-semibold text-violet-900 hover:bg-violet-100">
              <Sparkles className="h-4 w-4" /> Draft with AI
            </button>
            <button type="button" onClick={startNew} className="inline-flex items-center justify-center gap-2 rounded-xl bg-teal-700 px-4 py-2.5 font-semibold text-white hover:bg-teal-600">
              <Plus className="h-4 w-4" /> New entry
            </button>
          </div>
        </div>
        {draftOpen ? (
          <div className="mt-5 rounded-2xl border border-violet-200 bg-violet-50/70 p-4">
            <h3 className="font-bold text-violet-950">Create a plain-language draft</h3>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Paste a short, sanitized description of what happened and what changed. Remove names, emails, secrets, raw user messages, and long logs. AI creates a draft only; nothing is saved until you approve it.
            </p>
            <textarea
              value={draftNotes}
              onChange={(event) => setDraftNotes(event.target.value)}
              maxLength="6000"
              className={`${formControlClass} mt-3 h-32`}
              placeholder="Example: Account deletion failed in staging because the browser blocked DELETE requests. We updated the allowed methods, added a test, and confirmed deletion worked."
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" disabled={drafting || draftNotes.trim().length < 20} onClick={createDraft} className="inline-flex items-center gap-2 rounded-xl bg-violet-700 px-4 py-2 font-semibold text-white disabled:opacity-50">
                <Sparkles className="h-4 w-4" /> {drafting ? 'Writing draft...' : 'Create draft'}
              </button>
              <button type="button" onClick={() => setDraftOpen(false)} className="rounded-xl border border-slate-300 px-4 py-2 font-semibold text-slate-700">Cancel</button>
            </div>
          </div>
        ) : null}
        <div className="mt-5 flex flex-wrap gap-2">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800">
            <option value="all">All types</option>
            {TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800">
            <option value="all">All statuses</option>
            {STATUSES.map((value) => <option key={value} value={value}>{displayLabel(value)}</option>)}
          </select>
          <select value={environmentFilter} onChange={(e) => setEnvironmentFilter(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800">
            <option value="all">All environments</option>
            {ENVIRONMENTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <button
            type="button"
            onClick={async () => {
              const next = !archived;
              setArchived(next);
              await load(next);
            }}
            className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            {archived ? 'Show active entries' : 'Show archived entries'}
          </button>
          <span className="self-center text-sm text-slate-500">{filtered.length} entries</span>
        </div>
        {message ? <p className="mt-3 text-sm text-teal-700">{message}</p> : null}
        {error && !editing ? <p className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700">{error}</p> : null}
      </div>

      {editing ? (
        <form ref={editorRef} onSubmit={save} className="scroll-mt-6 rounded-3xl border border-emerald-500/30 bg-white/80 p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-slate-900">{editing === 'new' ? 'New archive entry' : 'Edit archive entry'}</h3>
            <button type="button" onClick={() => { setEditing(null); setEditorError(''); }} aria-label="Close editor" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
          <p className="mt-2 text-sm text-slate-500">Use plain language. Write as if you are explaining this to a new teammate who was not there.</p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <label className="text-sm font-semibold">Date<input required type="date" value={form.eventDate} onChange={(e) => setForm({ ...form, eventDate: e.target.value })} className={formControlClass} /></label>
            <label className="text-sm font-semibold">Title<input required maxLength="120" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={formControlClass} placeholder="Short and easy to scan" /></label>
            <label className="text-sm font-semibold">Feature<input required maxLength="80" value={form.feature} onChange={(e) => setForm({ ...form, feature: e.target.value })} className={formControlClass} placeholder="Accounts, Product lookup, Deployment..." /></label>
            <label className="text-sm font-semibold">Type<select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={formControlClass}>{TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="text-sm font-semibold">Status<select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={formControlClass}>{STATUSES.map((value) => <option key={value} value={value}>{displayLabel(value)}</option>)}</select></label>
            <label className="text-sm font-semibold">Environment<select value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} className={formControlClass}>{ENVIRONMENTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <label className="text-sm font-semibold md:col-span-2">Summary<textarea required maxLength="500" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className={`${formControlClass} h-20`} placeholder="What happened? Keep this to one or two simple sentences." /></label>
            <label className="text-sm font-semibold md:col-span-2">Outcome<textarea required maxLength="500" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} className={`${formControlClass} h-20`} placeholder="What changed or was verified?" /></label>
            <label className="text-sm font-semibold md:col-span-2">Lesson<textarea maxLength="500" value={form.lesson} onChange={(e) => setForm({ ...form, lesson: e.target.value })} className={`${formControlClass} h-20`} placeholder="What should we remember next time?" /></label>
            <div className="text-sm font-semibold">
              Tags
              <div className="mt-1 flex gap-2">
                <input
                  value={tagDraft}
                  onChange={(event) => setTagDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addTag();
                    }
                  }}
                  className={`${formControlClass} mt-0`}
                  placeholder="Type one tag"
                />
                <button type="button" onClick={addTag} className="rounded-xl border border-slate-600 bg-slate-800 px-4 font-semibold text-slate-100 hover:bg-slate-700">
                  Add
                </button>
              </div>
              <p className="mt-1 text-xs font-normal text-slate-500">Enter one tag at a time, then press Return or click Add.</p>
              {(form.tags || []).length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {form.tags.map((tag) => (
                    <button key={tag} type="button" onClick={() => removeTag(tag)} className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-200 hover:bg-red-500/20 hover:text-red-200" title="Remove tag">
                      {tag} <X className="h-3 w-3" />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <label className="text-sm font-semibold">Related commit or link<input maxLength="500" value={form.relatedLink} onChange={(e) => setForm({ ...form, relatedLink: e.target.value })} className={formControlClass} placeholder="Optional commit hash or https:// link" /></label>
            <label className="text-sm font-semibold md:col-span-2">Next step<textarea maxLength="500" value={form.nextStep} onChange={(e) => setForm({ ...form, nextStep: e.target.value })} className={`${formControlClass} h-20`} placeholder="Only needed when more work remains." /></label>
            <label className="text-sm font-semibold md:col-span-2">Expanded details<textarea maxLength="5000" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className={`${formControlClass} h-36`} placeholder="Optional sanitized symptoms, reproduction steps, decisions, and reference IDs. No emails, secrets, raw messages, or long logs." /></label>
          </div>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button disabled={loading} type="submit" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50"><Save className="h-4 w-4" /> Save entry</button>
            {editorError ? <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-200">{editorError}</p> : null}
          </div>
        </form>
      ) : null}

      {loading && items.length === 0 ? <p className="text-sm text-slate-500">Loading Learning Archive...</p> : null}
      {!loading && filtered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-200 bg-white/40 p-8 text-center text-sm text-slate-500">No entries match these filters.</div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {filtered.map((item) => {
            const expanded = expandedId === item.id;
            return (
              <article key={item.id} className="rounded-3xl border border-slate-200 bg-white/60 p-5 shadow-xl shadow-black/10">
                <p className="text-sm font-semibold text-slate-500">{formatEventDate(item.eventDate)}</p>
                <h3 className="mt-1 text-xl font-bold text-slate-900">{item.title}</h3>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <FieldBadge label="Feature" value={item.feature} tone="blue" />
                  <FieldBadge label="Type" value={item.type} tone="amber" />
                  <FieldBadge label="Status" value={item.status} tone="green" />
                  <FieldBadge label="Environment" value={item.environment} tone="purple" />
                </div>
                <div className="mt-5 space-y-3 text-sm leading-6">
                  <div><p className="font-bold text-slate-800">Summary</p><p className="text-slate-600">{item.summary}</p></div>
                  <div><p className="font-bold text-slate-800">Outcome</p><p className="text-slate-600">{item.outcome}</p></div>
                </div>
                {item.tags?.length ? <div className="mt-4 flex flex-wrap gap-2">{item.tags.map((tag) => <span key={tag} className="rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-white">{tag}</span>)}</div> : null}
                {expanded ? (
                  <div className="mt-5 space-y-3 border-t border-slate-200 pt-4 text-sm">
                    {item.lesson ? <div><p className="font-bold text-slate-800">Lesson</p><p className="whitespace-pre-wrap text-slate-600">{item.lesson}</p></div> : null}
                    {item.nextStep ? <div><p className="font-bold text-slate-800">Next step</p><p className="whitespace-pre-wrap text-slate-600">{item.nextStep}</p></div> : null}
                    {item.details ? <div><p className="font-bold text-slate-800">Details</p><p className="whitespace-pre-wrap text-slate-600">{item.details}</p></div> : null}
                    {item.relatedLink ? <div><p className="font-bold text-slate-800">Related commit or link</p><p className="break-all text-teal-700">{item.relatedLink}</p></div> : null}
                  </div>
                ) : null}
                <div className="mt-5 flex flex-wrap gap-2">
                  <button type="button" onClick={() => setExpandedId(expanded ? null : item.id)} className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} {expanded ? 'Hide details' : 'Expand details'}
                  </button>
                  <button type="button" onClick={() => startEdit(item)} className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Edit3 className="h-4 w-4" /> Edit</button>
                  <button type="button" onClick={() => changeArchived(item, !archived)} className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    {archived ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />} {archived ? 'Restore' : 'Archive'}
                  </button>
                  {archived ? (
                    confirmingDeleteId === item.id ? (
                      <>
                        <button type="button" onClick={() => permanentlyDelete(item)} className="inline-flex items-center gap-1 rounded-xl bg-red-500 px-3 py-2 text-sm font-semibold text-white hover:bg-red-400">
                          <Trash2 className="h-4 w-4" /> Confirm permanent delete
                        </button>
                        <button type="button" onClick={() => setConfirmingDeleteId(null)} className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                      </>
                    ) : (
                      <button type="button" onClick={() => setConfirmingDeleteId(item.id)} className="inline-flex items-center gap-1 rounded-xl border border-red-300 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" /> Permanently delete
                      </button>
                    )
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
